import { ContestStatus } from '@betterpool/api-types/contests-service';
import { Group, Stack } from '@mantine/core';
import { IconInfoCircle, IconX } from '@tabler/icons-react';
import { format } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';

import { Text } from '~/domains/design-system/Text';
import { BadgeIcons } from '~/components/shared/Badges/Badges';
import { CurrencyConvertor } from '~/components/utils/formatters';
import { Routes } from '~/domains/common/constants/routes';
import ContestTypeBadge from '~/domains/contest/domains/common/components/ContestTypeBadge/ContestTypeBadge';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';

import getContestMaxEntriesLabel from '../../utils/getContestMaxEntriesLabel';

import useStyles from './GamecenterHeader.styles';

function formatStartDate(
  startDate: Date,
  status: ContestStatus,
  t: ReturnType<typeof useTranslation<'contest'>>['t']
) {
  if (status !== ContestStatus.SCHEDULED) {
    return format(startDate, 'M/d');
  }

  return t('gamecenter.header.startsAt', { startDate: format(startDate, 'E M/d, h:mm a') });
}

function GamecenterHeader({ closeHref, contest }: { closeHref: string; contest: FullContest }) {
  const { classes, theme } = useStyles();
  const { t } = useTranslation('contest');
  const isEntertainmentOnly = contest.settings?.entertainmentOnlySettings?.enabled;
  const maxEntries = getContestMaxEntriesLabel(contest.entries);

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <Link href={closeHref} className={classes.closeLink} title={t('gamecenter.header.close')}>
          <IconX width={24} height={24} color={theme.white} />
        </Link>
        <div className={classes.desktop}>
          <Group position="apart" w="100%" h="100%">
            <div className={classes.info}>
              <Link href={Routes.contestDetail(contest.id)} className={classes.titleLink}>
                <Group align="center" spacing={8}>
                  <Text variant="headline-large">{contest.name}</Text>
                  <IconInfoCircle size={20} className={classes.titleIcon} />
                </Group>
              </Link>
              <Group c="gray.4" spacing={8}>
                <Text variant="body-medium">
                  {formatStartDate(contest.start_date, contest.status, t)}
                </Text>
                <Text variant="body-medium">•</Text>
                <ContestTypeBadge type={contest.contest_type} colorScheme="dark" />
                <BadgeIcons wrapStyle="" tags={contest.tags} iconSize={20} />
              </Group>
            </div>
            <div className={classes.stats}>
              <div className={classes.stat}>
                <Text variant="headline-medium" c="white">
                  {contest.entries.filled}/{maxEntries}
                </Text>
                <Text variant="body-medium" c="gray.4">
                  {typeof contest.entries.user === 'number'
                    ? t('gamecenter.header.entries', {
                        filled: contest.entries.user,
                        max: contest.entries.max_per_user,
                      })
                    : t('gamecenter.header.entriesAnon')}
                </Text>
              </div>
              {!isEntertainmentOnly && (
                <>
                  <div className={classes.stat}>
                    <Text variant="headline-medium" c="white">
                      {CurrencyConvertor(contest.entry_fee / 100)}
                    </Text>
                    <Text variant="body-medium" c="gray.4">
                      {t('gamecenter.header.entry')}
                    </Text>
                  </div>
                  <div className={classes.stat}>
                    <Text variant="headline-medium" c="white">
                      {CurrencyConvertor(contest.prize_pool / 100)}
                    </Text>
                    <Text variant="body-medium" c="gray.4">
                      {t('gamecenter.header.prizes')}
                    </Text>
                  </div>
                </>
              )}
            </div>
          </Group>
        </div>
        <div className={classes.mobile}>
          <Stack align="center" justify="center" h="100%" spacing={4}>
            <Text variant="headline-large" c="white" lh={1.2} truncate ta="center" maw="100%">
              {contest.name}
            </Text>
            <Text variant="body-small" c="gray.4" lh={1} mt={-2}>
              {formatStartDate(contest.start_date, contest.status, t)}
            </Text>
          </Stack>
        </div>
        <div className={classes.mobile}>
          <Link href={Routes.contestDetail(contest.id)} className={classes.mobileInfoLink}>
            <IconInfoCircle size={24} color="white" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default GamecenterHeader;
